import { getAuthToken } from './auth/tokenStorage';
import axios from 'axios';
import { API_URL } from '../config';
import { vaultLogger } from '../utils/logger';
import { redactError } from '../utils/redactError';
import {
  openCredentialFields,
  openManyCredentialFields,
  PASSWORD_RECORD_FIELDS,
  PORTAL_RECORD_FIELDS,
  sealCredentialFields,
} from './encryption/credentialFields';

/**
 * Vault Service for HashiCorp Vault Integration
 * 
 * This service provides a frontend interface to interact with HashiCorp Vault
 * through a backend API. The backend API handles the actual Vault operations
 * while this service provides a clean interface for the React Native app.
 * 
 * All sensitive data (passwords, secret codes, etc.) are stored securely in
 * HashiCorp Vault through the backend API.
 */

// Use the same API URL as the rest of the app
const API_BASE_URL = API_URL;

class VaultService {
  // Helper method to get auth headers
  private async getAuthHeaders() {
    vaultLogger.debug('=== FRONTEND VAULT SERVICE: Getting auth token ===');
    const token = await getAuthToken();
    vaultLogger.debug('Token found:', token ? 'YES' : 'NO');
    if (!token) {
      vaultLogger.error('=== FRONTEND VAULT SERVICE: No authentication token found ===');
      throw new Error('No authentication token found');
    }
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    vaultLogger.debug('=== FRONTEND VAULT SERVICE: Auth headers created ===');
    return headers;
  }

  // Password management methods
  async storePasswordCredentials(
    passwordId: string,
    data: {
      category: string;
      passwordType: string;
      username: string;
      password: string;
      websiteUrl: string;
      notes?: string;
    },
  ): Promise<void> {
    vaultLogger.debug('=== FRONTEND VAULT SERVICE: storePasswordCredentials called ===');
    vaultLogger.debug('Password ID:', passwordId);
    vaultLogger.debug('API URL:', `${API_BASE_URL}/api/vault/passwords/${passwordId}`);
    vaultLogger.debug('Request data:', {
      category: data.category,
      passwordType: data.passwordType,
      username: data.username,
      password: '[HIDDEN]',
      websiteUrl: data.websiteUrl,
      notes: data.notes,
    });
    
    // K5: the login fields leave the device encrypted (RecoveryCodeRequiredError
    // / VaultLockedError propagate to the screen unchanged).
    const body = await sealCredentialFields(data, PASSWORD_RECORD_FIELDS);
    try {
      vaultLogger.debug('=== FRONTEND VAULT SERVICE: Getting auth headers ===');
      const headers = await this.getAuthHeaders();
      vaultLogger.debug('Auth headers:', { ...headers, Authorization: headers.Authorization ? 'Bearer [HIDDEN]' : 'NOT SET' });

      vaultLogger.debug('=== FRONTEND VAULT SERVICE: Making API request ===');
      const response = await axios.post(`${API_BASE_URL}/api/vault/passwords/${passwordId}`, body, { headers });
      vaultLogger.debug('=== FRONTEND VAULT SERVICE: API response received ===');
      vaultLogger.debug('Response status:', response.status);
      vaultLogger.debug('Response data:', response.data);
      
      if (response.status !== 200 && response.status !== 201) {
        vaultLogger.error('=== FRONTEND VAULT SERVICE: Invalid response status ===', response.status);
        throw new Error('Failed to store password credentials');
      }
      
      vaultLogger.debug('=== FRONTEND VAULT SERVICE: storePasswordCredentials succeeded ===');
    } catch (error: any) {
      vaultLogger.error('=== FRONTEND VAULT SERVICE: Error storing password credentials ===', redactError(error));
      throw new Error('Failed to store password credentials');
    }
  }

  async getPasswordCredentials(
    passwordId: string,
    targetUserId?: string,
  ): Promise<{
    category: string;
    passwordType: string;
    username: string;
    password: string;
    websiteUrl: string;
    notes: string;
    ownerUserId?: string;
    locked?: boolean;
  }> {
    try {
      const headers = await this.getAuthHeaders();
      const params = targetUserId ? `?targetUserId=${encodeURIComponent(targetUserId)}` : '';
      const response = await axios.get(`${API_BASE_URL}/api/vault/passwords/${passwordId}${params}`, { headers });
      return await openCredentialFields(response.data, PASSWORD_RECORD_FIELDS, {
        ownerUserId: response.data?.ownerUserId ?? targetUserId,
      });
    } catch (error) {
      vaultLogger.error('Error retrieving password credentials from Vault:', redactError(error));
      throw new Error('Failed to retrieve password credentials');
    }
  }

  async deletePasswordCredentials(passwordId: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.delete(`${API_BASE_URL}/api/vault/passwords/${passwordId}`, { headers });
      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Failed to delete password credentials');
      }
    } catch (error) {
      vaultLogger.error('Error deleting password credentials from Vault:', redactError(error));
      throw new Error('Failed to delete password credentials');
    }
  }

  async getAllPasswordsByCategory(category: string, targetUserId?: string, opts: { quiet?: boolean } = {}): Promise<Array<{
    id: string;
    category: string;
    passwordType: string;
    username: string;
    password: string;
    websiteUrl: string;
    notes: string;
    locked?: boolean;
  }>> {
    try {
      const headers = await this.getAuthHeaders();
      const params = targetUserId ? `?targetUserId=${encodeURIComponent(targetUserId)}` : '';
      const response = await axios.get(`${API_BASE_URL}/api/vault/passwords/category/${category}${params}`, { headers });
      return await this.openPasswordRecords(response.data, targetUserId, opts.quiet);
    } catch (error: any) {
      // If it's a 404 error, the path doesn't exist yet, which is fine
      if (error.response && error.response.status === 404) {
        vaultLogger.debug('Passwords category path does not exist yet in vault, returning empty list');
        return [];
      }
      // Any other error (network, server) surfaces as an empty list here so
      // the screen can render without crashing. The plaintext AsyncStorage
      // fallback that used to live here has been removed — we never write
      // passwords off-vault again. See docs/KNOWN_LIMITATIONS.md.
      vaultLogger.error('Error retrieving passwords by category from Vault:', redactError(error));
      return [];
    }
  }

  /** Decrypt a list of password records; all belong to one owner. */
  private async openPasswordRecords<T extends Record<string, unknown>>(
    records: T[],
    targetUserId?: string,
    quiet = false,
  ): Promise<T[]> {
    if (!Array.isArray(records) || records.length === 0) return records;
    const ownerUserId = (records[0] as any)?.ownerUserId ?? targetUserId;
    return openManyCredentialFields(records, PASSWORD_RECORD_FIELDS, { ownerUserId, prompt: !quiet });
  }

  /**
   * `quiet`: a read that only counts (dashboard, checklist progress) must
   * not ask for the Recovery Code; a locked vault blanks the fields instead.
   */
  async getAllPasswords(targetUserId?: string, opts: { quiet?: boolean } = {}): Promise<Array<{
    id: string;
    category: string;
    passwordType: string;
    username: string;
    password: string;
    websiteUrl: string;
    notes: string;
    locked?: boolean;
  }>> {
    try {
      const headers = await this.getAuthHeaders();
      const params = targetUserId ? `?targetUserId=${encodeURIComponent(targetUserId)}` : '';
      const response = await axios.get(`${API_BASE_URL}/api/vault/passwords${params}`, { headers });
      return await this.openPasswordRecords(response.data, targetUserId, opts.quiet);
    } catch (error: any) {
      // If it's a 404 error, the path doesn't exist yet, which is fine
      if (error.response && error.response.status === 404) {
        vaultLogger.debug('Passwords path does not exist yet in vault, returning empty list');
        return [];
      }
      // Suppress network errors to prevent console spam - this is expected when Vault API is not available
      if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
        vaultLogger.debug('Vault API not available (expected in development), using local fallback');
        return [];
      }
      vaultLogger.error('Error retrieving all passwords from Vault:', redactError(error));
      return [];
    }
  }

  // Accountant credentials methods
  async storeAccountantCredentials(
    accountantId: string,
    data: {
      credentials: string;
      licenseNumber: string;
    },
  ): Promise<void> {
    vaultLogger.debug('=== FRONTEND VAULT SERVICE: storeAccountantCredentials called ===');
    vaultLogger.debug('Accountant ID:', accountantId);
    
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.post(`${API_BASE_URL}/api/vault/accountant/${accountantId}`, data, { headers });
      
      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Failed to store accountant credentials');
      }
      
      vaultLogger.debug('=== FRONTEND VAULT SERVICE: storeAccountantCredentials succeeded ===');
    } catch (error: any) {
      vaultLogger.error('=== FRONTEND VAULT SERVICE: Error storing accountant credentials ===', redactError(error));
      throw new Error('Failed to store accountant credentials');
    }
  }

  async getAccountantCredentials(
    accountantId: string,
  ): Promise<{
    credentials: string;
    licenseNumber: string;
  }> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}/api/vault/accountant/${accountantId}`, { headers });
      return response.data;
    } catch (error) {
      vaultLogger.error('Error retrieving accountant credentials from Vault:', redactError(error));
      throw new Error('Failed to retrieve accountant credentials');
    }
  }

  async deleteAccountantCredentials(accountantId: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.delete(`${API_BASE_URL}/api/vault/accountant/${accountantId}`, { headers });
      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Failed to delete accountant credentials');
      }
    } catch (error) {
      vaultLogger.error('Error deleting accountant credentials from Vault:', redactError(error));
      throw new Error('Failed to delete accountant credentials');
    }
  }

  // Attorney credentials methods
  async storeAttorneyCredentials(
    attorneyId: string,
    data: {
      credentials: string;
      licenseNumber: string;
    },
  ): Promise<void> {
    vaultLogger.debug('=== FRONTEND VAULT SERVICE: storeAttorneyCredentials called ===');
    vaultLogger.debug('Attorney ID:', attorneyId);
    
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.post(`${API_BASE_URL}/api/vault/attorney/${attorneyId}`, data, { headers });
      
      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Failed to store attorney credentials');
      }
      
      vaultLogger.debug('=== FRONTEND VAULT SERVICE: storeAttorneyCredentials succeeded ===');
    } catch (error: any) {
      vaultLogger.error('=== FRONTEND VAULT SERVICE: Error storing attorney credentials ===', redactError(error));
      throw new Error('Failed to store attorney credentials');
    }
  }

  async getAttorneyCredentials(
    attorneyId: string,
  ): Promise<{
    credentials: string;
    licenseNumber: string;
  }> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}/api/vault/attorney/${attorneyId}`, { headers });
      return response.data;
    } catch (error) {
      vaultLogger.error('Error retrieving attorney credentials from Vault:', redactError(error));
      throw new Error('Failed to retrieve attorney credentials');
    }
  }

  async deleteAttorneyCredentials(attorneyId: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.delete(`${API_BASE_URL}/api/vault/attorney/${attorneyId}`, { headers });
      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Failed to delete attorney credentials');
      }
    } catch (error) {
      vaultLogger.error('Error deleting attorney credentials from Vault:', redactError(error));
      throw new Error('Failed to delete attorney credentials');
    }
  }

  // Investment Advisor credentials methods  
  async storeInvestmentAdvisorCredentials(
    advisorId: string,
    data: {
      credentials: string;
      licenseNumber: string;
    },
  ): Promise<void> {
    vaultLogger.debug('=== FRONTEND VAULT SERVICE: storeInvestmentAdvisorCredentials called ===');
    vaultLogger.debug('Investment Advisor ID:', advisorId);
    
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.post(`${API_BASE_URL}/api/vault/investment-advisor/${advisorId}`, data, { headers });
      
      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Failed to store investment advisor credentials');
      }
      
      vaultLogger.debug('=== FRONTEND VAULT SERVICE: storeInvestmentAdvisorCredentials succeeded ===');
    } catch (error: any) {
      vaultLogger.error('=== FRONTEND VAULT SERVICE: Error storing investment advisor credentials ===', redactError(error));
      throw new Error('Failed to store investment advisor credentials');
    }
  }

  async getInvestmentAdvisorCredentials(
    advisorId: string,
  ): Promise<{
    credentials: string;
    licenseNumber: string;
  }> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}/api/vault/investment-advisor/${advisorId}`, { headers });
      return response.data;
    } catch (error) {
      vaultLogger.error('Error retrieving investment advisor credentials from Vault:', redactError(error));
      throw new Error('Failed to retrieve investment advisor credentials');
    }
  }

  async deleteInvestmentAdvisorCredentials(advisorId: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.delete(`${API_BASE_URL}/api/vault/investment-advisor/${advisorId}`, { headers });
      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Failed to delete investment advisor credentials');
      }
    } catch (error) {
      vaultLogger.error('Error deleting investment advisor credentials from Vault:', redactError(error));
      throw new Error('Failed to delete investment advisor credentials');
    }
  }

  // NEW: Portal credentials methods with proper field names and automatic migration
  async storePortalCredentials(
    entityId: string,
    entityType: 'accountant' | 'attorney' | 'investment-advisor' | 'insurance-agent' | 'tax-advisor' | 'credit-card' | 'investments' | 'loans' | 'crypto' | 'loan' | 'banking' | 'homeowners-insurance' | 'gas-provider' | 'electric-provider' | 'streaming-service-provider' | 'lawn-service-provider' | 'pest-control-provider' | 'pool-service-provider' | 'gym-membership' | 'primary_physician',
    data: {
      username?: string;
      password?: string;
      accountNumber?: string;
      privateKey?: string;
      seedPhrase?: string;
    },
  ): Promise<void> {
    vaultLogger.debug('=== FRONTEND VAULT SERVICE: storePortalCredentials called ===');
    vaultLogger.debug('Entity Type:', entityType);
    vaultLogger.debug('Entity ID:', entityId);

    // K5: encrypted on the device before it leaves (errors propagate).
    const body = await sealCredentialFields(data as Record<string, unknown>, PORTAL_RECORD_FIELDS);
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.post(`${API_BASE_URL}/api/vault/portal/${entityType}/${entityId}`, body, { headers });
      
      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Failed to store portal credentials');
      }
      
      vaultLogger.debug('=== FRONTEND VAULT SERVICE: storePortalCredentials succeeded ===');
    } catch (error: any) {
      vaultLogger.error('=== FRONTEND VAULT SERVICE: Error storing portal credentials ===', redactError(error));
      throw new Error('Failed to store portal credentials');
    }
  }

  async getPortalCredentials(
    entityId: string,
    entityType: 'accountant' | 'attorney' | 'investment-advisor' | 'insurance-agent' | 'tax-advisor' | 'credit-card' | 'investments' | 'loans' | 'crypto' | 'loan' | 'banking' | 'homeowners-insurance' | 'gas-provider' | 'electric-provider' | 'streaming-service-provider' | 'lawn-service-provider' | 'pest-control-provider' | 'pool-service-provider' | 'gym-membership' | 'primary_physician'
  ): Promise<{
    username?: string;
    password?: string;
    accountNumber?: string;
    phonePasscode?: string;
    privateKey?: string;
    seedPhrase?: string;
    masterCode?: string;
    userCodes?: string;
    ownerUserId?: string;
    locked?: boolean;
  } | null> {
    try {
      const headers = await this.getAuthHeaders();

      // First try new portal credentials endpoint
      try {
        const response = await axios.get(`${API_BASE_URL}/api/vault/portal/${entityType}/${entityId}`, { headers });
        const d = response.data;
        if (d && PORTAL_RECORD_FIELDS.some((f) => d[f])) {
          vaultLogger.debug('=== PORTAL CREDENTIALS: Found data in new schema ===');
          // K5: decrypt with the owner's key (the response says whose it is).
          return await openCredentialFields(d, PORTAL_RECORD_FIELDS, { ownerUserId: d.ownerUserId });
        }
      } catch (error: any) {
        // 404 is expected when no new data exists yet - this is normal
        if (error.response?.status === 404) {
          vaultLogger.debug('=== PORTAL CREDENTIALS: New schema empty (404), trying migration ===');
        } else {
          vaultLogger.debug('=== PORTAL CREDENTIALS: New schema error, trying migration ===', error.message);
        }
      }

      // If new schema doesn't have data, try to migrate from old schema
      vaultLogger.debug('=== PORTAL CREDENTIALS: Attempting migration from old schema ===');
      try {
        const oldData = entityType === 'accountant'
          ? await this.getAccountantCredentials(entityId)
          : entityType === 'attorney'
          ? await this.getAttorneyCredentials(entityId)
          : entityType === 'investment-advisor'
          ? null  // investment-advisor doesn't have old credentials to migrate
          : entityType === 'tax-advisor'
          ? null  // tax-advisor doesn't have old credentials to migrate
          : entityType === 'credit-card'
          ? null  // credit-card doesn't have old credentials to migrate
          : entityType === 'investments'
          ? null  // investments doesn't have old credentials to migrate
          : entityType === 'loans'
          ? null  // loans doesn't have old credentials to migrate
          : entityType === 'gym-membership'
          ? null  // gym-membership doesn't have old credentials to migrate
          : null; // insurance-agent doesn't have old credentials to migrate

        vaultLogger.debug('=== PORTAL CREDENTIALS: Old data retrieved ===', {
          hasCredentials: !!oldData?.credentials,
          hasLicenseNumber: !!oldData?.licenseNumber,
          credentials: oldData?.credentials ? '[HIDDEN]' : 'NONE',
          licenseNumber: oldData?.licenseNumber ? '[HIDDEN]' : 'NONE'
        });

        if (oldData && (oldData.credentials || oldData.licenseNumber)) {
          vaultLogger.debug('=== PORTAL CREDENTIALS: Found old data, migrating to new schema ===');
          const migratedData = {
            username: oldData.credentials || '',
            password: oldData.licenseNumber || ''
          };
          
          vaultLogger.debug('=== PORTAL CREDENTIALS: Storing migrated data ===', {
            username: migratedData.username ? '[HIDDEN]' : 'EMPTY',
            password: migratedData.password ? '[HIDDEN]' : 'EMPTY'
          });
          
          // Store in new schema
          await this.storePortalCredentials(entityId, entityType, migratedData);
          vaultLogger.debug('=== PORTAL CREDENTIALS: Migration completed successfully ===');
          return migratedData;
        } else {
          vaultLogger.debug('=== PORTAL CREDENTIALS: No old data found to migrate ===');
        }
      } catch (error: any) {
        vaultLogger.debug('=== PORTAL CREDENTIALS: Migration failed ===', redactError(error));
      }

      return { username: '', password: '' };
    } catch (error) {
      vaultLogger.error('Error retrieving portal credentials from Vault:', redactError(error));
      return { username: '', password: '' };
    }
  }

  async deletePortalCredentials(entityId: string, entityType: 'accountant' | 'attorney' | 'investment-advisor' | 'insurance-agent' | 'tax-advisor' | 'credit-card' | 'investments' | 'loans' | 'crypto' | 'loan' | 'banking' | 'homeowners-insurance' | 'gas-provider' | 'electric-provider' | 'streaming-service-provider' | 'lawn-service-provider' | 'pest-control-provider' | 'pool-service-provider' | 'gym-membership' | 'primary_physician'): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.delete(`${API_BASE_URL}/api/vault/portal/${entityType}/${entityId}`, { headers });
      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Failed to delete portal credentials');
      }
    } catch (error) {
      vaultLogger.error('Error deleting portal credentials from Vault:', redactError(error));
      throw new Error('Failed to delete portal credentials');
    }
  }

  // Document metadata management methods (for metadata-only documents without file uploads)
  async storeDocumentMetadata(
    documentId: string,
    data: {
      category: string;
      documentType: string;
      documentName: string;
      location: string;
      purpose?: string;
    },
  ): Promise<void> {
    vaultLogger.debug('=== FRONTEND VAULT SERVICE: storeDocumentMetadata called ===');
    vaultLogger.debug('Document ID:', documentId);
    vaultLogger.debug('API URL:', `${API_BASE_URL}/api/vault/documents/${documentId}`);
    vaultLogger.debug('Request data:', {
      category: data.category,
      documentType: data.documentType,
      documentName: data.documentName,
      location: data.location,
      purpose: data.purpose,
    });

    try {
      vaultLogger.debug('=== FRONTEND VAULT SERVICE: Getting auth headers ===');
      const headers = await this.getAuthHeaders();

      vaultLogger.debug('=== FRONTEND VAULT SERVICE: Making API request ===');
      const response = await axios.post(`${API_BASE_URL}/api/vault/documents/${documentId}`, data, { headers });
      vaultLogger.debug('=== FRONTEND VAULT SERVICE: API response received ===');
      vaultLogger.debug('Response status:', response.status);
      vaultLogger.debug('Response data:', response.data);

      if (response.status !== 200 && response.status !== 201) {
        vaultLogger.error('=== FRONTEND VAULT SERVICE: Invalid response status ===', response.status);
        throw new Error('Failed to store document metadata');
      }

      vaultLogger.debug('=== FRONTEND VAULT SERVICE: storeDocumentMetadata succeeded ===');
    } catch (error: any) {
      vaultLogger.error('=== FRONTEND VAULT SERVICE: Error storing document metadata ===', redactError(error));
      throw new Error('Failed to store document metadata');
    }
  }

  async getDocumentMetadata(
    documentId: string,
  ): Promise<{
    category: string;
    documentType: string;
    documentName: string;
    location: string;
    purpose?: string;
  }> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}/api/vault/documents/${documentId}`, { headers });
      return response.data;
    } catch (error) {
      vaultLogger.error('Error retrieving document metadata from Vault:', redactError(error));
      throw new Error('Failed to retrieve document metadata');
    }
  }

  async deleteDocumentMetadata(documentId: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.delete(`${API_BASE_URL}/api/vault/documents/${documentId}`, { headers });
      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Failed to delete document metadata');
      }
    } catch (error) {
      vaultLogger.error('Error deleting document metadata from Vault:', redactError(error));
      throw new Error('Failed to delete document metadata');
    }
  }

  async getAllDocumentsByCategory(category: string): Promise<Array<{
    id: string;
    category: string;
    documentType: string;
    documentName: string;
    location: string;
    purpose?: string;
  }>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}/api/vault/documents/category/${category}`, { headers });
      return response.data;
    } catch (error: any) {
      // If it's a 404 error, the path doesn't exist yet, which is fine
      if (error.response && error.response.status === 404) {
        vaultLogger.debug('Documents category path does not exist yet in vault, returning empty list');
        return [];
      }
      // Suppress network errors to prevent console spam - this is expected when Vault API is not available
      if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
        vaultLogger.debug('Vault API not available (expected in development), using local fallback');
        return [];
      }
      vaultLogger.error('Error retrieving documents by category from Vault:', redactError(error));
      return [];
    }
  }

  async getAllDocuments(): Promise<Array<{
    id: string;
    category: string;
    documentType: string;
    documentName: string;
    location: string;
    purpose?: string;
  }>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}/api/vault/documents`, { headers });
      return response.data;
    } catch (error: any) {
      // If it's a 404 error, the path doesn't exist yet, which is fine
      if (error.response && error.response.status === 404) {
        vaultLogger.debug('Documents path does not exist yet in vault, returning empty list');
        return [];
      }
      // Suppress network errors to prevent console spam - this is expected when Vault API is not available
      if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
        vaultLogger.debug('Vault API not available (expected in development), using local fallback');
        return [];
      }
      vaultLogger.error('Error retrieving all documents from Vault:', redactError(error));
      return [];
    }
  }

  // =====================================================
  // Document Encryption Key Management
  // These methods store document encryption keys securely in Vault
  // for better security and cloud backup capability
  // =====================================================

  /**
   * Store the master recovery key in Vault
   * This key is used to encrypt/decrypt individual document keys
   */
  async storeMasterRecoveryKey(masterKey: string): Promise<void> {
    vaultLogger.debug('=== VAULT SERVICE: storeMasterRecoveryKey called ===');

    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.post(
        `${API_BASE_URL}/api/vault/document-keys/master`,
        { masterKey },
        { headers }
      );

      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Failed to store master recovery key');
      }

      vaultLogger.debug('=== VAULT SERVICE: Master recovery key stored successfully ===');
    } catch (error: any) {
      // Plaintext AsyncStorage fallback removed (see docs/KNOWN_LIMITATIONS.md).
      // Surface the failure so the caller can prompt the user to retry online.
      vaultLogger.error(
        '=== VAULT SERVICE: Error storing master recovery key ===',
        redactError(error),
      );
      throw new Error('Failed to store master recovery key');
    }
  }

  /**
   * Retrieve the master recovery key from Vault
   * Falls back to local storage if Vault is unavailable
   */
  async getMasterRecoveryKey(): Promise<string | null> {
    vaultLogger.debug('=== VAULT SERVICE: getMasterRecoveryKey called ===');

    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.get(
        `${API_BASE_URL}/api/vault/document-keys/master`,
        { headers }
      );

      if (response.data && response.data.masterKey) {
        vaultLogger.debug('=== VAULT SERVICE: Master recovery key retrieved from Vault ===');
        return response.data.masterKey;
      }

      return null;
    } catch (error: any) {
      // 404 → no master key has been enrolled yet.
      if (error.response?.status === 404) {
        return null;
      }
      // Plaintext AsyncStorage fallback removed. Surface the failure.
      vaultLogger.error(
        '=== VAULT SERVICE: Error retrieving master recovery key ===',
        redactError(error),
      );
      throw new Error('Failed to retrieve master recovery key');
    }
  }

  /**
   * Store a document-specific encryption key in Vault
   * Each document has its own key, encrypted with the master key
   */
  async storeDocumentEncryptionKey(documentId: string, encryptedKey: string): Promise<void> {
    vaultLogger.debug('=== VAULT SERVICE: storeDocumentEncryptionKey called ===');
    vaultLogger.debug('Document ID:', documentId);

    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.post(
        `${API_BASE_URL}/api/vault/document-keys/${documentId}`,
        { encryptedKey },
        { headers }
      );

      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Failed to store document encryption key');
      }

      vaultLogger.debug('=== VAULT SERVICE: Document encryption key stored successfully ===');
    } catch (error: any) {
      // Plaintext AsyncStorage fallback removed. Surface the failure so the
      // caller can retry or abort the surrounding document upload.
      vaultLogger.error(
        '=== VAULT SERVICE: Error storing document encryption key ===',
        redactError(error),
      );
      throw new Error('Failed to store document encryption key');
    }
  }

  /**
   * Retrieve a document-specific encryption key from Vault
   */
  async getDocumentEncryptionKey(documentId: string): Promise<string | null> {
    vaultLogger.debug('=== VAULT SERVICE: getDocumentEncryptionKey called ===');
    vaultLogger.debug('Document ID:', documentId);

    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.get(
        `${API_BASE_URL}/api/vault/document-keys/${documentId}`,
        { headers }
      );

      if (response.data && response.data.encryptedKey) {
        vaultLogger.debug('=== VAULT SERVICE: Document encryption key retrieved from Vault ===');
        return response.data.encryptedKey;
      }

      return null;
    } catch (error: any) {
      // 404 → no key enrolled for this document.
      if (error.response?.status === 404) {
        return null;
      }
      // Plaintext AsyncStorage fallback removed.
      vaultLogger.error(
        '=== VAULT SERVICE: Error retrieving document encryption key ===',
        redactError(error),
      );
      throw new Error('Failed to retrieve document encryption key');
    }
  }

  /**
   * Delete a document-specific encryption key from Vault
   */
  async deleteDocumentEncryptionKey(documentId: string): Promise<void> {
    vaultLogger.debug('=== VAULT SERVICE: deleteDocumentEncryptionKey called ===');
    vaultLogger.debug('Document ID:', documentId);

    try {
      const headers = await this.getAuthHeaders();
      await axios.delete(
        `${API_BASE_URL}/api/vault/document-keys/${documentId}`,
        { headers }
      );

      vaultLogger.debug('=== VAULT SERVICE: Document encryption key deleted from Vault ===');
    } catch (error: any) {
      // Plaintext AsyncStorage fallback removed — there is no local copy to
      // clean up. 404 is benign (already gone); anything else propagates.
      if (error.response?.status === 404) return;
      vaultLogger.error(
        '=== VAULT SERVICE: Error deleting document encryption key ===',
        redactError(error),
      );
      throw new Error('Failed to delete document encryption key');
    }
  }

  // =====================================================
  // Step 5 — wrapped master key (primary-user flows)
  //
  // The backend only ever sees the WRAPPED bytes + the verifier. It
  // cannot unwrap without the user's secret code. See
  // docs/KEY_WRAPPING_DESIGN.md for the full design.
  // =====================================================

  // =====================================================
  // K5 — migration of existing plaintext logins (design §5). The owner's
  // device reads its records, encrypts what is still plaintext, writes each
  // record back and asks to be marked migrated; the server verifies.
  // =====================================================

  async getCredentialMigrationStatus(): Promise<{ credentialsEncryptedAt: string | null }> {
    const headers = await this.getAuthHeaders();
    const response = await axios.get(`${API_BASE_URL}/api/vault/credentials/status`, { headers });
    return { credentialsEncryptedAt: response.data?.credentialsEncryptedAt ?? null };
  }

  async getCredentialInventory(): Promise<{
    credentialsEncryptedAt: string | null;
    records: Array<{
      path: string;
      kind: string;
      entityType?: string;
      id: string;
      secretFields: string[];
      data: Record<string, unknown>;
    }>;
  }> {
    const headers = await this.getAuthHeaders();
    const response = await axios.get(`${API_BASE_URL}/api/vault/credentials/inventory`, { headers });
    return {
      credentialsEncryptedAt: response.data?.credentialsEncryptedAt ?? null,
      records: Array.isArray(response.data?.records) ? response.data.records : [],
    };
  }

  async rewriteCredentialSecret(path: string, data: Record<string, unknown>): Promise<void> {
    const headers = await this.getAuthHeaders();
    const response = await axios.put(`${API_BASE_URL}/api/vault/credentials/rewrite`, { path, data }, { headers });
    if (response.status !== 200 && response.status !== 201) {
      throw new Error('Failed to rewrite a saved login');
    }
  }

  /** Null when the server still found plaintext (it answers 409 with counts). */
  async markCredentialsMigrated(): Promise<{ credentialsEncryptedAt: string | null; plaintextFields: number }> {
    const headers = await this.getAuthHeaders();
    try {
      const response = await axios.post(`${API_BASE_URL}/api/vault/credentials/migrated`, {}, { headers });
      return { credentialsEncryptedAt: response.data?.credentialsEncryptedAt ?? null, plaintextFields: 0 };
    } catch (error: any) {
      if (error?.response?.status === 409) {
        return { credentialsEncryptedAt: null, plaintextFields: Number(error.response.data?.plaintextFields) || 0 };
      }
      throw error;
    }
  }

  async storeWrappedMasterKey(payload: {
    wrappedMasterKey: string;
    wrapVersion: string;
    verifier: string;
    userKdfSalt: string;
  }): Promise<void> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${API_BASE_URL}/api/vault/wrapped-master-key`,
      payload,
      { headers },
    );
    if (response.status !== 200 && response.status !== 201) {
      throw new Error('Failed to store wrapped master key');
    }
  }

  /**
   * Atomic enrollment write: persists the wrapped master key AND the
   * wrapping keypair in a single backend save. Use this from
   * MasterKeyService.enrollMasterKey / migrateLegacyMasterKey instead of
   * calling storeWrappedMasterKey + storeWrappingKeypair separately —
   * the two-call sequence has a half-enrollment window where a network
   * blip between the two POSTs can leave wrapped_master_key set but
   * wrapping_public_key NULL.
   *
   * The standalone endpoints remain available for the lazy-enroll path
   * on subsequent unlocks (when the user already has a wrapped master
   * key but lacks a wrapping keypair) and for changeSecretCode rotation.
   */
  async storeWrappedMasterKeyWithKeypair(payload: {
    wrappedMasterKey: string;
    wrapVersion: string;
    verifier: string;
    userKdfSalt: string;
    wrappingPublicKey: string;
    wrappedWrappingSecretKey: string;
  }): Promise<void> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${API_BASE_URL}/api/vault/wrapped-master-key/with-keypair`,
      payload,
      { headers },
    );
    if (response.status !== 200 && response.status !== 201) {
      throw new Error('Failed to store wrapped master key with keypair');
    }
  }

  /**
   * Fetch the wrapped master key triple.
   * Returns `null` if the user has not enrolled yet (backend 404) —
   * that's the signal for the client to run the silent migration from
   * the legacy `/document-keys/master` path.
   */
  async getWrappedMasterKey(): Promise<{
    wrappedMasterKey: string;
    wrapVersion: string;
    userKdfSalt: string;
  } | null> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.get(
        `${API_BASE_URL}/api/vault/wrapped-master-key`,
        { headers },
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      vaultLogger.error(
        'Error fetching wrapped master key:',
        redactError(error),
      );
      throw new Error('Failed to fetch wrapped master key');
    }
  }

  /**
   * Fetch just the KDF salt — used during a new-device login so the
   * client can derive the KEK before pulling the wrapped key. Returns
   * null if the user has not enrolled.
   */
  async getKdfSalt(): Promise<string | null> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.get(
        `${API_BASE_URL}/api/vault/wrapped-master-key/kdf-salt`,
        { headers },
      );
      return response.data?.userKdfSalt ?? null;
    } catch (error: any) {
      vaultLogger.error('Error fetching KDF salt:', redactError(error));
      throw new Error('Failed to fetch KDF salt');
    }
  }

  /**
   * Rotate the wrapped master key as part of a secret-code change.
   * Client must prove knowledge of the old KEK via `oldVerifier`; the
   * backend refuses the overwrite without it.
   */
  async changeWrappedMasterKey(payload: {
    oldVerifier: string;
    newWrappedMasterKey: string;
    newWrapVersion: string;
    newVerifier: string;
  }): Promise<void> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${API_BASE_URL}/api/vault/wrapped-master-key/change`,
      payload,
      { headers },
    );
    if (response.status !== 200 && response.status !== 201) {
      throw new Error('Failed to change wrapped master key');
    }
  }

  // =====================================================
  // Wrapping keypair (trusted-party handshake)
  // =====================================================

  async storeWrappingKeypair(payload: {
    wrappingPublicKey: string;
    wrappedWrappingSecretKey: string;
  }): Promise<void> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${API_BASE_URL}/api/vault/wrapping-keypair`,
      payload,
      { headers },
    );
    if (response.status !== 200 && response.status !== 201) {
      throw new Error('Failed to store wrapping keypair');
    }
  }

  async getWrappingKeypair(): Promise<{
    wrappingPublicKey: string;
    wrappedWrappingSecretKey: string;
  } | null> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.get(
        `${API_BASE_URL}/api/vault/wrapping-keypair`,
        { headers },
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      vaultLogger.error(
        'Error fetching wrapping keypair:',
        redactError(error),
      );
      throw new Error('Failed to fetch wrapping keypair');
    }
  }

  // =====================================================
  // Trusted-party wrapped keys
  // =====================================================

  /**
   * Primary uploads a sealed master key for a specific trusted party.
   * Called from PendingConnectionsScreen after the user taps "Complete"
   * on a pending invitee.
   */
  async storeTrustedPartyWrappedKey(payload: {
    trustedPartyUserId: string;
    role: 'successor' | 'keyholder';
    wrappedMasterKey: string;
    wrapVersion: string;
  }): Promise<void> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${API_BASE_URL}/api/vault/trusted-party-wrapped-key`,
      payload,
      { headers },
    );
    if (response.status !== 200 && response.status !== 201) {
      throw new Error('Failed to store trusted-party wrapped key');
    }
  }

  /**
   * Trusted party (successor post-death / keyholder with an active grant)
   * fetches their sealed copy of the primary's master key.
   * Returns null on 403 (not yet gated in) or 404 (no row).
   */
  async getTrustedPartyWrappedKey(primaryUserId: string): Promise<{
    wrappedMasterKey: string;
    wrapVersion: string;
    role: 'successor' | 'keyholder';
    createdAt: string;
  } | null> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.get(
        `${API_BASE_URL}/api/vault/trusted-party-wrapped-key/${encodeURIComponent(primaryUserId)}`,
        { headers },
      );
      return response.data;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 403 || status === 404) return null;
      vaultLogger.error(
        'Error fetching trusted-party wrapped key:',
        redactError(error),
      );
      throw new Error('Failed to fetch trusted-party wrapped key');
    }
  }

  /**
   * Primary lists relationships that still need an async handshake wrap.
   */
  async listPendingTrustedPartyHandshakes(): Promise<Array<{
    relationshipId: string;
    trustedPartyUserId: string;
    trustedPartyEmail: string;
    trustedPartyFirstName: string | null;
    trustedPartyLastName: string | null;
    role: 'successor' | 'keyholder';
    wrappingPublicKey: string;
    createdAt: string;
  }>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.get(
        `${API_BASE_URL}/api/vault/trusted-party-wrapped-key-pending`,
        { headers },
      );
      return response.data ?? [];
    } catch (error: any) {
      vaultLogger.error(
        'Error listing pending trusted-party handshakes:',
        redactError(error),
      );
      throw new Error('Failed to list pending handshakes');
    }
  }
}

export const vaultService = new VaultService();
